// ─── Visual editing ───────────────────────────────────────────────────────────
// Direct manipulation of the preview document, with no model call anywhere in
// the loop. The generated site is literal HTML and CSS, so "make this heading
// bigger" is a style write on one node — not a prompt, not a patch, not a
// credit. A refinement round-trip costs a few hundred to fifty thousand output
// tokens and takes seconds to a minute; this costs nothing and lands instantly.
//
// The rule that keeps it honest: the preview document is the source of truth
// only while edit mode is on. On commit the body is serialised straight back
// into the page's html, so the saved source and the pixels never diverge.

import { videoEmbed, EMBED_ALLOW } from '@/lib/videoEmbed';

export const SELECTED_ATTR = 'data-bl-selected';
export const EDITOR_MARK   = 'data-bl-editor';
const EDITOR_STYLE_ID      = 'bl-editor-css';
const GENERATED_SCRIPT     = 'data-bl-generated';

// Elements a click should never select. Picking <body> gives the user a
// selection they cannot style meaningfully, and the shell's own nodes are not
// part of the generated site at all.
const UNSELECTABLE = new Set(['BODY', 'HTML', 'HEAD', 'SCRIPT', 'STYLE', 'LINK', 'META', 'TITLE']);

// Outline rather than border: a border changes layout, which would make every
// selection nudge the page. Outline is painted outside the box and costs
// nothing geometrically.
const EDITOR_CSS = `
  [${SELECTED_ATTR}] {
    outline: 2px solid #f97316 !important;
    outline-offset: 1px !important;
    cursor: text !important;
  }
  [${SELECTED_ATTR}]:focus { outline-color: #ea580c !important; }
  body.bl-editing *:hover:not([${SELECTED_ATTR}]) {
    outline: 1px dashed rgba(249,115,22,.55) !important;
    outline-offset: 1px !important;
  }
  body.bl-editing { cursor: default !important; }
  /* A YouTube or Vimeo player is another site's page: a click on it never
     reaches this document, so it could not be selected to replace. While
     editing, clicks pass through it and mediaAt() finds it by position. */
  body.bl-editing iframe { pointer-events: none !important; }
`;

/** Turn the editor's affordances on inside the frame's document. */
export function injectEditorStyle(doc) {
  if (!doc?.head || doc.getElementById(EDITOR_STYLE_ID)) return;
  const style = doc.createElement('style');
  style.id = EDITOR_STYLE_ID;
  style.setAttribute(EDITOR_MARK, '');
  style.textContent = EDITOR_CSS;
  doc.head.appendChild(style);
  doc.body?.classList.add('bl-editing');
}

export function removeEditorStyle(doc) {
  if (!doc) return;
  doc.getElementById(EDITOR_STYLE_ID)?.remove();
  doc.body?.classList.remove('bl-editing');
  clearSelection(doc);
}

export function clearSelection(doc) {
  if (!doc) return;
  doc.querySelectorAll(`[${SELECTED_ATTR}]`).forEach((el) => {
    el.removeAttribute(SELECTED_ATTR);
    el.removeAttribute('contenteditable');
  });
}

/** Walk up from a click target to the nearest node worth selecting. */
export function selectableFrom(node) {
  let el = node;
  while (el && el.nodeType !== Node.ELEMENT_NODE) el = el.parentNode;
  while (el && UNSELECTABLE.has(el.nodeName)) el = el.parentElement;
  return el && !UNSELECTABLE.has(el.nodeName) ? el : null;
}

export function select(doc, el) {
  clearSelection(doc);
  if (!el) return null;
  el.setAttribute(SELECTED_ATTR, '');
  // Only leaf-ish text nodes become directly typeable. Making a <section>
  // contenteditable lets a stray keystroke delete half the page.
  if (holdsOnlyText(el)) el.setAttribute('contenteditable', 'true');
  return el;
}

/** True when an element's content is text the user can safely type over. */
export function holdsOnlyText(el) {
  if (!el || !el.childNodes.length) return false;
  return Array.from(el.childNodes).every(
    (n) => n.nodeType === Node.TEXT_NODE ||
           (n.nodeType === Node.ELEMENT_NODE && /^(BR|B|I|EM|STRONG|SPAN|SMALL|U)$/.test(n.nodeName)),
  );
}

/** A short human label for the current selection, e.g. "h1.hero-title". */
export function describe(el) {
  if (!el) return '';
  const tag = el.nodeName.toLowerCase();
  const cls = (el.getAttribute('class') || '')
    .split(/\s+/).filter(Boolean).slice(0, 2).map((c) => `.${c}`).join('');
  return `${tag}${cls}`;
}

/** Ancestor chain, nearest last, so the UI can offer "select the parent". */
export function ancestorTrail(el, limit = 4) {
  const trail = [];
  let cur = el?.parentElement;
  while (cur && !UNSELECTABLE.has(cur.nodeName) && trail.length < limit) {
    trail.unshift(cur);
    cur = cur.parentElement;
  }
  return trail;
}

/**
 * Write one CSS property as an inline style.
 *
 * Inline beats a Tailwind utility class in the cascade, so an edit sticks
 * without having to find and rewrite the class that set the value. An empty
 * value removes the property again rather than writing an empty declaration.
 */
export function applyStyle(el, prop, value) {
  if (!el) return;
  if (value === '' || value == null) el.style.removeProperty(prop);
  else el.style.setProperty(prop, value);
}

/** What the element currently renders as, whether from a class or inline. */
export function readStyle(el, prop) {
  if (!el) return '';
  const doc = el.ownerDocument;
  const view = doc?.defaultView;
  if (!view) return '';
  return view.getComputedStyle(el).getPropertyValue(prop) || '';
}

/** Computed colour comes back as rgb(); the colour input needs #rrggbb. */
export function toHex(colour) {
  const m = String(colour || '').match(/rgba?\(([^)]+)\)/i);
  if (!m) return /^#[0-9a-f]{6}$/i.test(colour) ? colour : '#000000';
  const [r, g, b] = m[1].split(',').map((n) => Math.max(0, Math.min(255, parseInt(n, 10) || 0)));
  return `#${[r, g, b].map((n) => n.toString(16).padStart(2, '0')).join('')}`;
}

/** Strip "px" for a number input, tolerating "auto" and percentages. */
export function toPx(value) {
  const n = parseFloat(value);
  return Number.isFinite(n) ? Math.round(n) : 0;
}

/** Delete the selection and hand back the parent, so selection moves up
 *  rather than vanishing — unless the parent is the body itself. */
export function removeElement(el) {
  const parent = el?.parentElement || null;
  el?.remove();
  return parent && !UNSELECTABLE.has(parent.nodeName) ? parent : null;
}

export function duplicateElement(el) {
  if (!el?.parentElement) return null;
  const copy = el.cloneNode(true);
  copy.removeAttribute(SELECTED_ATTR);
  copy.removeAttribute('contenteditable');
  el.parentElement.insertBefore(copy, el.nextSibling);
  return copy;
}

/**
 * Read the edited document back out as page HTML.
 *
 * Everything the preview added for its own purposes has to come off first, or
 * it would be written into the user's saved source: the scripts livePreview.js
 * injects to run the generated JS (which is stored separately as script.js),
 * the editor's own stylesheet, and the selection bookkeeping.
 */
/** True when this node is an image the user can swap out. */
export function isImage(el) {
  return !!el && el.nodeName === 'IMG';
}

// A replaced image is no longer the photograph we fetched, so the request that
// produced it has to go with it. `data-photo` is the description a re-resolve
// would search on — a new provider, a dead link — and leaving it in place means
// the next resolve quietly overwrites the picture the user chose. `srcset` and
// `sizes` matter for a different reason: the browser prefers them over `src`,
// so a new src alone would appear to do nothing at all on any image the model
// wrote responsively.
const PHOTO_ATTRS = ['data-photo', 'srcset', 'sizes'];

/**
 * Point an <img> at a new image.
 *
 * @param {HTMLImageElement} el
 * @param {string} src an http(s) URL or a data: URL
 * @returns {boolean} whether anything changed
 */
export function replaceImage(el, src) {
  if (!isImage(el)) return false;
  const next = String(src || '').trim();
  // Anything that is not plainly an image location is refused rather than
  // written: `javascript:` in a src is the obvious one, but a stray `blob:`
  // would also break the moment the page is saved and reopened, because the
  // object URL belongs to the session that created it.
  if (!/^(https?:\/\/|data:image\/|\/)/i.test(next)) return false;

  PHOTO_ATTRS.forEach((a) => el.removeAttribute(a));
  el.setAttribute('src', next);
  // A hand-picked image is almost never the aspect the generated one was, and
  // the width/height attributes exist to reserve layout space. Dropping the
  // ratio lock keeps the new picture from being squashed into the old frame.
  el.style.objectFit = el.style.objectFit || 'cover';
  return true;
}

/** Whether any pixel on the canvas is less than fully opaque. */
export function hasTransparency(ctx, width, height) {
  const { data } = ctx.getImageData(0, 0, width, height);
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] < 255) return true;
  }
  return false;
}

/**
 * Read a file the user picked into a data: URL, made web-sized first, ready
 * for uploadImage.
 *
 * Downscaled to a sane web dimension, which is invisible at the sizes these
 * images are shown. Anything without transparency is re-encoded as JPEG: a
 * photograph saved as PNG compresses badly, and a 1920×1080 PNG photo stays
 * megabytes even at 1600px. A PNG or WebP that does use transparency keeps it.
 * An animated GIF is passed through untouched, because a canvas keeps only its
 * first frame.
 *
 * @param {File} file
 * @param {{maxDim?: number, quality?: number}} [opts]
 * @returns {Promise<string>} a data: URL
 */
export function fileToDataUrl(file, { maxDim = 1600, quality = 0.82 } = {}) {
  return new Promise((resolve, reject) => {
    if (!file || !/^image\//.test(file.type)) {
      reject(new Error('That file is not an image.'));
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => reject(new Error('That file could not be read.'));
    reader.onload = () => {
      if (/^image\/gif$/i.test(file.type)) { resolve(String(reader.result)); return; }
      const img = new Image();
      img.onerror = () => reject(new Error('That image could not be decoded.'));
      img.onload = () => {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));

        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) { resolve(String(reader.result)); return; }
        ctx.drawImage(img, 0, 0, w, h);

        // JPEG unless the picture actually uses transparency, which JPEG would
        // fill with black. Pixels that cannot be read count as transparent:
        // keeping a PNG is the safe way to be wrong.
        let type = 'image/jpeg';
        if (!/jpe?g/i.test(file.type)) {
          try { if (hasTransparency(ctx, w, h)) type = 'image/png'; } catch { type = 'image/png'; }
        }
        try {
          resolve(canvas.toDataURL(type, quality));
        } catch {
          // A canvas tainted by something unexpected still has the original.
          resolve(String(reader.result));
        }
      };
      img.src = String(reader.result);
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Upload a picked image and return the URL it is served at.
 *
 * The visual editor used to inline the picture into the page as a data: URL,
 * so the picture's size became the page's: a 3.76 MB PNG failed the save as
 * "pages exceeds the 2 MB limit". Even a picture that fitted was re-sent to the
 * model as base64 with every AI edit afterwards. Uploaded, the page holds only
 * a URL, the same arrangement a replaced video always had.
 *
 * @param {File} file
 * @param {{request: Function, encode?: Function}} deps  `request` is authFetch
 * @returns {Promise<string>} the public URL
 */
export async function uploadImage(file, { request, encode = fileToDataUrl }) {
  const dataUrl = await encode(file);
  const { publicUrl } = await request('/api/upload', {
    method: 'POST',
    body: JSON.stringify({ data_url: dataUrl, folder: 'media' }),
  });
  if (!publicUrl) throw new Error('That image could not be uploaded.');
  return publicUrl;
}

/** True when this node is a video the user can swap out. */
export function isVideo(el) {
  return !!el && el.nodeName === 'VIDEO';
}

// A replaced video keeps none of what described the old one. `data-video` is
// the query a re-resolve would search on, and `poster` is a still frame OF THE
// CLIP BEING REPLACED — left behind it shows the old video until the new one
// starts playing, which looks like the replacement silently failed.
const VIDEO_ATTRS = ['data-video', 'poster'];

/**
 * Point a <video> at a new file.
 *
 * Handles both shapes the markup comes in. `src` on the element is what the
 * generator is told to write, but a model reaches for `<source>` children often
 * enough that ignoring them would make this appear to do nothing: the browser
 * prefers a <source> over the element's own src, exactly as srcset beats src on
 * an image.
 *
 * @param {HTMLVideoElement} el
 * @param {string} src an http(s) URL, a site-relative path, or a data: URL
 * @returns {boolean} whether anything changed
 */
export function replaceVideo(el, src) {
  if (!isVideo(el)) return false;
  const next = String(src || '').trim();
  // Same rule as an image: `javascript:` is refused outright, and a `blob:`
  // URL belongs to the session that made it and dies on the next page load.
  if (!/^(https?:\/\/|data:video\/|\/)/i.test(next)) return false;

  VIDEO_ATTRS.forEach((a) => el.removeAttribute(a));
  el.querySelectorAll('source').forEach((s) => s.remove());
  el.setAttribute('src', next);

  // A replaced clip is almost always meant to behave like the one it replaced:
  // silent, looping, and playing inline rather than taking over the screen on
  // iOS. Only added where absent, so a deliberate choice is never overridden.
  if (!el.hasAttribute('muted')) { el.setAttribute('muted', ''); el.muted = true; }
  if (!el.hasAttribute('playsinline')) el.setAttribute('playsinline', '');

  // Re-read the new source. Without this the element keeps showing the old
  // frame until something else forces it.
  try { el.load(); } catch { /* not every environment implements it */ }
  return true;
}

/** True when this node is a YouTube or Vimeo player. */
export function isEmbed(el) {
  return !!el && el.nodeName === 'IFRAME' && !!videoEmbed(el.getAttribute('src'));
}

/** Anything the Replace controls work on: a picture, a video or a player. */
export function isMedia(el) {
  return isImage(el) || isVideo(el) || isEmbed(el);
}

/** Whether a video plays with controls and sound, rather than as a silent loop. */
export function hasControls(el) {
  return isVideo(el) && el.hasAttribute('controls');
}

// A silent loop behaves like a moving picture: it starts by itself, which a
// browser only allows when muted, and plays inline, or iOS takes it full
// screen. With controls it waits for a press and plays with its sound.
function setPlayback(video, controls) {
  if (controls) {
    video.setAttribute('controls', '');
    ['autoplay', 'loop', 'muted'].forEach((a) => video.removeAttribute(a));
    video.muted = false;
  } else {
    video.removeAttribute('controls');
    video.setAttribute('autoplay', '');
    video.setAttribute('loop', '');
    video.setAttribute('muted', '');
    video.muted = true;
  }
  video.setAttribute('playsinline', '');
  video.setAttribute('preload', 'metadata');
}

/** Switch a video between a silent loop (false) and controls with sound (true). */
export function setVideoControls(el, controls) {
  if (!isVideo(el)) return false;
  setPlayback(el, controls);
  try { el.load(); } catch { /* not every environment implements it */ }
  return true;
}

/** Where it sits and how big: the attributes a new kind of media keeps. */
function carryLayout(from, to, { size = true } = {}) {
  ['id', 'class', 'style', ...(size ? ['width', 'height'] : [])].forEach((a) => {
    const v = from.getAttribute(a);
    if (v != null) to.setAttribute(a, v);
  });
  to.removeAttribute(SELECTED_ATTR);
  to.removeAttribute('contenteditable');
}

/**
 * Put a video where a picture, a player or another video is.
 *
 * The picture's place and size are kept, so a hero photograph becomes a hero
 * video. A video replaced by another keeps how it played; anything else
 * becomes a silent loop unless asked for controls.
 *
 * @param {Element} el  an <img>, <video> or player <iframe>
 * @param {string} src  the video's https:// address, or a site path
 * @param {{controls?: boolean}} [opts]
 * @returns {HTMLVideoElement|null} the video now in the page
 */
export function setVideo(el, src, { controls } = {}) {
  if (!isMedia(el)) return null;
  const next = String(src || '').trim();
  if (!/^(https?:\/\/|\/)/i.test(next)) return null;
  if (isVideo(el)) {
    const current = /** @type {HTMLVideoElement} */ (el);
    if (!replaceVideo(current, next)) return null;
    if (controls !== undefined) setVideoControls(current, controls);
    return current;
  }
  const video = el.ownerDocument.createElement('video');
  carryLayout(el, video);
  const label = el.getAttribute('alt') || el.getAttribute('title');
  if (label) video.setAttribute('aria-label', label);
  video.setAttribute('src', next);
  setPlayback(video, !!controls);
  video.style.objectFit = video.style.objectFit || 'cover';
  el.replaceWith(video);
  return video;
}

/**
 * Put a YouTube or Vimeo player where a picture or a video is.
 *
 * The same attributes the server gives a player it keeps (sanitizer.js):
 * `referrerpolicy` matters most, because every page we serve sends no
 * referrer, and YouTube's player refuses to play without one.
 *
 * @param {Element} el    an <img>, <video> or player <iframe>
 * @param {string} link   any YouTube or Vimeo link
 * @returns {HTMLIFrameElement|null} the player now in the page
 */
export function setEmbed(el, link) {
  if (!isMedia(el)) return null;
  const embed = videoEmbed(link);
  if (!embed) return null;
  const replacing = !isEmbed(el);
  const frame = /** @type {HTMLIFrameElement} */ (replacing ? el.ownerDocument.createElement('iframe') : el);
  if (replacing) {
    // Not width and height: on a picture they only reserve its shape, and on
    // an iframe they would fix its size outright.
    carryLayout(el, frame, { size: false });
    frame.setAttribute('title', el.getAttribute('alt') || el.getAttribute('title')
      || (embed.provider === 'vimeo' ? 'Vimeo video' : 'YouTube video'));
  }
  frame.setAttribute('src', embed.src);
  frame.setAttribute('allow', EMBED_ALLOW);
  frame.setAttribute('allowfullscreen', '');
  frame.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
  frame.setAttribute('loading', 'lazy');
  // A player is 16:9; where the picture had no height of its own, the ratio
  // gives it one. object-fit means nothing to an iframe.
  frame.style.removeProperty('object-fit');
  if (!frame.style.aspectRatio) frame.style.aspectRatio = '16 / 9';
  frame.style.border = '0';
  if (!/(^|\s)(w-|absolute|inset-)/.test(frame.getAttribute('class') || '')) frame.style.width = '100%';
  if (replacing) el.replaceWith(frame);
  return frame;
}

/** Whether a node is drawn at all, judged from it and a few ancestors. */
function isShown(el) {
  const view = el.ownerDocument?.defaultView;
  if (!view) return true;
  for (let n = el, depth = 0; n && n.nodeType === 1 && depth < 5; n = n.parentElement, depth += 1) {
    const s = view.getComputedStyle(n);
    if (s.display === 'none' || s.visibility === 'hidden' || s.opacity === '0') return false;
  }
  return true;
}

/**
 * The picture, video or player under a point, if any; the smallest when they
 * overlap.
 *
 * A hero video sits under its scrim, its gradient and its copy, and is often
 * pointer-events: none, so a click there landed on whatever was on top and
 * "Replace video" never appeared: the complaint was that pictures could be
 * replaced and videos could not. Found by position rather than hit-testing,
 * which skips exactly the elements that take no clicks.
 *
 * @param {Document} doc
 * @param {number} x  client coordinates in that document
 * @param {number} y
 * @returns {Element|null}
 */
export function mediaAt(doc, x, y) {
  let best = null;
  let bestArea = Infinity;
  doc.querySelectorAll('img, video, iframe').forEach((el) => {
    if (!isMedia(el)) return;
    const r = el.getBoundingClientRect();
    if (!r.width || !r.height || x < r.left || x > r.right || y < r.top || y > r.bottom) return;
    const area = r.width * r.height;
    if (area < bestArea && isShown(el)) { best = el; bestArea = area; }
  });
  return best;
}

/**
 * What a click in the editor selects. Usually the element clicked; but where
 * that is not text and a picture, video or player is under the pointer, the
 * media, because that is what a click on a hero is meant to reach. Text keeps
 * the click: a heading over a hero video is edited, not the video.
 *
 * @param {Document} doc
 * @param {Element|null} target  the click's target
 * @param {number} x
 * @param {number} y
 */
export function pickAt(doc, target, x, y) {
  const el = selectableFrom(target);
  if (!el || isMedia(el) || holdsOnlyText(el)) return el;
  return mediaAt(doc, x, y) || el;
}

export function serializeBody(doc) {
  if (!doc?.body) return '';
  const clone = doc.body.cloneNode(true);

  clone.querySelectorAll(`script[${GENERATED_SCRIPT}], [${EDITOR_MARK}]`).forEach((n) => n.remove());
  clone.querySelectorAll(`[${SELECTED_ATTR}]`).forEach((n) => n.removeAttribute(SELECTED_ATTR));
  clone.querySelectorAll('[contenteditable]').forEach((n) => n.removeAttribute('contenteditable'));
  // The class is added to <body>, which is not serialised, but a cloned
  // subtree can carry it if the user duplicated something odd.
  clone.classList?.remove('bl-editing');
  // The preview's own entrance class and build wireframe (src/lib/livePreview.js,
  // src/lib/wireframe.js) are scaffolding. Neither may be saved into the site.
  clone.querySelectorAll('#bl-wire').forEach((n) => n.remove());
  clone.querySelectorAll('.bl-enter').forEach((n) => {
    n.classList.remove('bl-enter');
    if (!n.classList.length) n.removeAttribute('class');
  });

  return clone.innerHTML.trim();
}
